import { useContext } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import { inject, observer, PropTypes as MobXPropTypes } from 'mobx-react';
import { CYRILLIC_REGEXP } from 'components/common/PaymentForm/ProviderForms/ModelValidators/utils';
import i18nContext from 'components/i18n-context';
import ValidateBankDetailsModel from 'components/Templates/models/ValidateBankDetailsModel';
import { getTemplateStepErrorMessage } from 'components/Templates/utils/getTemplateStepErrorMessage';
import Button from 'uikit/Button/Button';
import Input from 'uikit/Input/Input';
import { InputDropDownSearch } from 'uikit/InputDropDown/InputDropDownSearch';
import './BankDetailsStep.scss';

const BankDetailsStep = ({ paymentStore, customerNumber, userAccounts, userStore }) => {
  const i18n = useContext(i18nContext);

  const form = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: false,
    initialValues: {
      bankName: paymentStore.templateData?.bankName || '',
      bankCountry: paymentStore.templateData?.bankCountry || '',
      bic: paymentStore.templateData?.bic || '',
      paymentPurpose: paymentStore.templateData?.paymentPurpose || ''
    },
    validationSchema: Yup.object().shape({
      bankName: Yup.string()
        .trim()
        .required(i18n.getMessage('client.validation.message.isEmpty'))
        .max(255, i18n.getMessage('client.validation.message.maxSize', { size: 255 }))
        .test(
          'no-cyrillic',
          i18n.getMessage('client.validation.message.noCyrillic'),
          (value) => !CYRILLIC_REGEXP.test(value || '')
        ),
      bankCountry: Yup.string().trim().required(i18n.getMessage('client.validation.message.isEmpty')),
      bic: Yup.string().trim().required(i18n.getMessage('client.validation.message.isEmpty')),
      paymentPurpose: Yup.string().when('bankCountry', (bankCountry, schema) => {
        if (bankCountry === 'AE' && paymentPurposeOptions.length > 0) {
          return schema.trim().required(i18n.getMessage('client.validation.message.isEmpty'));
        }
        return schema;
      })
    }),
    onSubmit: async (values) => {
      const requestData = ValidateBankDetailsModel({ ...paymentStore.templateData, ...values });

      paymentStore.setTemplateData({ ...paymentStore.templateData, ...values });

      await paymentStore.validateTemplateBankDetails(customerNumber, requestData);
    }
  });

  const { values, errors, handleSubmit, handleChange } = form;

  const allCodes = userAccounts?.flatMap((acct) => acct.countries) ?? [];
  const uniqueCodes = [...new Set(allCodes)];
  const countriesOptions = uniqueCodes.map((code) => ({
    key: code,
    value: i18n.getMessage(`country.${code}`)
  }));

  const handleBankCountryChange = (name, data) => handleChange({ target: { name, value: data } });

  // Payment purpose options for UAE based on country (no provider info available in Templates flow)
  const paymentPurposeOptions = (() => {
    if (values.bankCountry !== 'AE' || !userStore?.paymentPurposes?.length) {
      return [];
    }

    const providerData = userStore.paymentPurposes.find((item) => item.paymentPurposes?.[values.bankCountry]);
    if (!providerData) {
      return [];
    }

    const countryPurposes = providerData.paymentPurposes[values.bankCountry];

    return Object.entries(countryPurposes).map(([key, value]) => ({
      key: key,
      value: `${key} - ${value}`
    }));
  })();

  const handlePaymentPurposeChange = (name, data) => handleChange({ target: { name, value: data } });

  // Check if UAE is selected and payment purposes are available
  const isUAESelected = values.bankCountry === 'AE' && paymentPurposeOptions.length > 0;

  return (
    <form onSubmit={form.handleSubmit} className={'bank-details-step'}>
      <div className={'bank-details-step-inputs-wrapper'}>
        <Input
          className={'bank-details-step-input'}
          name={'bic'}
          label={i18n.getMessage('templates.form.bic.label')}
          placeholder={i18n.getMessage('templates.form.bic.placeholder')}
          value={values.bic}
          error={errors.bic || getTemplateStepErrorMessage(i18n, paymentStore.error, 'bic')}
          onChange={handleChange}
          isRequired
        />
        <Input
          className={'bank-details-step-input'}
          name={'bankName'}
          label={i18n.getMessage('templates.form.bankName.label')}
          placeholder={i18n.getMessage('templates.form.bankName.placeholder')}
          value={values.bankName}
          error={errors.bankName || getTemplateStepErrorMessage(i18n, paymentStore.error, 'bankName')}
          onChange={handleChange}
          isRequired
        />
        <InputDropDownSearch
          autoComplete={'off'}
          className={'bank-details-step-input'}
          name={'bankCountry'}
          label={i18n.getMessage('templates.form.bankCountry.label')}
          placeholder={i18n.getMessage('templates.form.bankCountry.placeholder')}
          options={countriesOptions}
          value={values.bankCountry}
          error={errors.bankCountry || getTemplateStepErrorMessage(i18n, paymentStore.error, 'bankCountry')}
          onChange={handleBankCountryChange}
          isRequired
        />
        {isUAESelected && (
          <InputDropDownSearch
            autoComplete={'off'}
            className={'bank-details-step-input'}
            name={'paymentPurpose'}
            label={i18n.getMessage('templates.form.paymentPurpose.label')}
            placeholder={i18n.getMessage('templates.form.paymentPurpose.placeholder')}
            options={paymentPurposeOptions}
            value={values.paymentPurpose}
            error={errors.paymentPurpose || getTemplateStepErrorMessage(i18n, paymentStore.error, 'paymentPurpose')}
            onChange={handlePaymentPurposeChange}
            isRequired
          />
        )}
      </div>
      <Button
        className={'bank-details-step-next-button'}
        type={'primary'}
        roleType={'submit'}
        onClick={handleSubmit}
        isDisabled={paymentStore.isLoading}
      >
        {i18n.getMessage('templates.form.button.next')}
      </Button>
    </form>
  );
};

BankDetailsStep.propTypes = {
  customerNumber: PropTypes.string,
  userAccounts: PropTypes.array,
  paymentStore: MobXPropTypes.observableObject,
  userStore: MobXPropTypes.observableObject
};

export default inject((stores) => ({
  paymentStore: stores.paymentStore,
  userStore: stores.userStore
}))(observer(BankDetailsStep));
